import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/ai-video-analyst";

const api = axios.create({ baseURL: baseUrl });

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const stripEmpty = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
};

const useAIVideoAnalyst = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (fn) => {
    setIsLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getVideos = useCallback(
    (filters = {}) =>
      run(async () => {
        const res = await api.get("/videos", { params: stripEmpty(filters) });
        return res.data;
      }),
    [run]
  );

  const addVideo = useCallback(
    (data) =>
      run(async () => {
        const res = await api.post("/videos", data);
        return res.data;
      }),
    [run]
  );

  const bulkAddVideos = useCallback(
    (videos) =>
      run(async () => {
        const res = await api.post("/videos/bulk", { videos });
        return res.data;
      }),
    [run]
  );

  const autoScoreVideos = useCallback(
    () =>
      run(async () => {
        const res = await api.post("/videos/auto-score");
        return res.data;
      }),
    [run]
  );

  const deleteVideo = useCallback(
    (id) =>
      run(async () => {
        const res = await api.delete(`/videos/${id}`);
        return res.data;
      }),
    [run]
  );

  const fetchUrlMetadata = useCallback(
    (url) =>
      run(async () => {
        const res = await api.post("/fetch-url", { url });
        return res.data;
      }),
    [run]
  );

  const exportVideosCsv = useCallback(
    async () => {
      const token = getAuthToken();
      const url = `${baseUrl}/videos/export`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export mislukt");
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `ai-video-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return true;
    },
    []
  );

  const getStats = useCallback(
    (filters = {}) =>
      run(async () => {
        const res = await api.get("/stats", { params: stripEmpty(filters) });
        return res.data;
      }),
    [run]
  );

  const getTrends = useCallback(
    (filters = {}) =>
      run(async () => {
        const res = await api.get("/trends", { params: stripEmpty(filters) });
        return res.data;
      }),
    [run]
  );

  const getBestPostTime = useCallback(
    (filters = {}) =>
      run(async () => {
        const res = await api.get("/best-post-time", { params: stripEmpty(filters) });
        return res.data;
      }),
    [run]
  );

  const getHookPatterns = useCallback(
    (filters = {}) =>
      run(async () => {
        const res = await api.get("/hook-patterns", { params: stripEmpty(filters) });
        return res.data;
      }),
    [run]
  );

  const getInsights = useCallback(
    (filters = {}) =>
      run(async () => {
        const res = await api.get("/insights", { params: stripEmpty(filters) });
        return res.data;
      }),
    [run]
  );

  const generateInsights = useCallback(
    ({ insightType, sector, country, dateFrom, dateTo }) =>
      run(async () => {
        const res = await api.post("/insights/generate", {
          insight_type: insightType,
          sector: sector || null,
          country: country || null,
          date_from: dateFrom || null,
          date_to: dateTo || null,
        });
        return res.data;
      }),
    [run]
  );

  const generateHooks = useCallback(
    (payload) =>
      run(async () => {
        const res = await api.post("/generate/hooks", stripEmpty(payload));
        return res.data;
      }),
    [run]
  );

  const generateScript = useCallback(
    (payload) =>
      run(async () => {
        const res = await api.post("/generate/script", stripEmpty(payload));
        return res.data;
      }),
    [run]
  );

  const previewScore = useCallback(
    (payload) =>
      run(async () => {
        const res = await api.post("/preview-score", stripEmpty(payload));
        return res.data;
      }),
    [run]
  );

  const savePreviewScore = useCallback(
    (data) =>
      run(async () => {
        const res = await api.post("/preview-score/save", data);
        return res.data;
      }),
    [run]
  );

  // ----- Credits -----

  // Credits use direct api calls (not run()) so they never affect isLoading/error state
  const getCredits = useCallback(async () => {
    const res = await api.get("/credits");
    return res.data;
  }, []);

  const buyCredits = useCallback(async (tierId) => {
    const res = await api.post("/credits/checkout", {
      tier_id: tierId,
      frontend_origin: window.location.origin,
    });
    return res.data;
  }, []);

  const verifyCredits = useCallback(async () => {
    const res = await api.post("/credits/verify");
    return res.data;
  }, []);

  // ----- Accounts -----

  const listAccounts = useCallback(
    () =>
      run(async () => {
        const res = await api.get("/accounts");
        return res.data;
      }),
    [run]
  );

  const connectMeta = useCallback(
    () =>
      run(async () => {
        const res = await api.post("/accounts/connect/meta");
        return res.data;
      }),
    [run]
  );

  const connectTikTok = useCallback(
    () =>
      run(async () => {
        const res = await api.post("/accounts/connect/tiktok");
        return res.data;
      }),
    [run]
  );

  const connectYouTube = useCallback(
    () =>
      run(async () => {
        const res = await api.post("/accounts/connect/youtube");
        return res.data;
      }),
    [run]
  );

  const syncAccount = useCallback(
    (id) =>
      run(async () => {
        const res = await api.post(`/accounts/${id}/sync`);
        return res.data;
      }),
    [run]
  );

  const deleteAccount = useCallback(
    (id) =>
      run(async () => {
        const res = await api.delete(`/accounts/${id}`);
        return res.data;
      }),
    [run]
  );

  return {
    discoverViral: useCallback(
      ({ platform, niche, language, period, limit } = {}) =>
        run(async () => {
          const res = await api.get("/discover/viral", {
            params: stripEmpty({ platform, niche, language, period, limit }),
          });
          return res.data;
        }),
      [run]
    ),
    analyseViralTrends: useCallback(
      (body) =>
        run(async () => {
          const res = await api.post("/discover/viral/analyse", body);
          return res.data;
        }),
      [run]
    ),
    generateViralIdea: useCallback(
      (body) =>
        run(async () => {
          const res = await api.post("/discover/viral/idea", body);
          return res.data;
        }),
      [run]
    ),
    getVideos,
    addVideo,
    bulkAddVideos,
    autoScoreVideos,
    deleteVideo,
    fetchUrlMetadata,
    exportVideosCsv,
    getStats,
    getTrends,
    getBestPostTime,
    getHookPatterns,
    getInsights,
    generateInsights,
    generateHooks,
    generateScript,
    previewScore,
    savePreviewScore,
    getCredits,
    buyCredits,
    verifyCredits,
    listAccounts,
    connectMeta,
    connectTikTok,
    connectYouTube,
    syncAccount,
    deleteAccount,
    isLoading,
    error,
  };
};

export default useAIVideoAnalyst;
